// v3.15-1198 COMP-005 — Master Compensation Ledger (CEO view).
//
// Executive read-only view of every compensation event recorded in the
// ledger. Restricted to PTC admin / Company Director / Manager Director.
// One row per ledger transaction; identical underlying record that the
// employee sees on their own page.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, ScrollText, Filter } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return iso; }
};

const StatusPill = ({ status }) => {
  const s = (status || 'pending').toLowerCase();
  const cls = s === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : s === 'voided' ? 'bg-slate-100 text-slate-600 border-slate-200'
    : 'bg-amber-50 text-amber-700 border-amber-200';
  return (
    <span
      data-testid={`master-status-${s}`}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${cls}`}
    >{s}</span>
  );
};

export default function MasterCompensationLedgerPage() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows]       = useState([]);
  const [error, setError]     = useState(null);

  const now = new Date();
  const [month, setMonth]   = useState(now.getMonth() + 1);
  const [year, setYear]     = useState(now.getFullYear());
  const [status, setStatus] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { month, year, limit: 500 };
      if (status !== 'all') params.status = status;
      const { data } = await api.get('/compensation-ledger/master', { params });
      setRows(data?.items || []);
    } catch (e) {
      if (e?.response?.status === 403) {
        setError('You do not have access to the CEO Master Money Manager.');
      } else {
        toast.error('Failed to load master compensation ledger');
      }
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [month, year, status]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totals = useMemo(() => {
    let total = 0, pending = 0, paid = 0;
    for (const r of rows) {
      const a = Number(r.amount || 0);
      total += a;
      if (r.status === 'paid')    paid    += a;
      if (r.status === 'pending') pending += a;
    }
    return { total, pending, paid, count: rows.length };
  }, [rows]);

  return (
    <div className="space-y-4" data-testid="master-compensation-page">
      <div className="flex items-center gap-3">
        <ScrollText className="w-6 h-6 text-violet-600" />
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">CEO Master Money Manager</h1>
          <p className="text-sm text-slate-500">
            Read-only ledger of every compensation event across the workforce.
            Money Manager does not calculate compensation — it records it.
          </p>
        </div>
      </div>

      {error ? (
        <div className="p-6 bg-rose-50 text-rose-700 border border-rose-200 rounded-xl"
             data-testid="master-forbidden">{error}</div>
      ) : (
        <>
          {/* Filters */}
          <div className="bg-white p-4 rounded-2xl border border-slate-200 flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase mb-1">Month</label>
              <select
                data-testid="filter-month"
                value={month} onChange={(e) => setMonth(Number(e.target.value))}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <option key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString('en-US', { month: 'long' })}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase mb-1">Year</label>
              <select
                data-testid="filter-year"
                value={year} onChange={(e) => setYear(Number(e.target.value))}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                {Array.from({ length: 5 }, (_, i) => now.getFullYear() - i).map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase mb-1">Status</label>
              <select
                data-testid="filter-status"
                value={status} onChange={(e) => setStatus(e.target.value)}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="voided">Voided</option>
              </select>
            </div>
            <button
              data-testid="filter-refresh"
              onClick={fetchData}
              className="ml-auto inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold px-4 py-2 rounded-lg">
              <Filter className="w-4 h-4" /> Apply
            </button>
          </div>

          {/* Totals */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div data-testid="master-total-count" className="rounded-2xl bg-white p-5 border-2 border-slate-200">
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Entries</div>
              <div className="text-3xl font-extrabold text-slate-800 mt-1">{totals.count}</div>
            </div>
            <div data-testid="master-total-pending" className="rounded-2xl bg-white p-5 border-2 border-amber-100">
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Pending</div>
              <div className="text-3xl font-extrabold text-amber-700 mt-1">{fmtMoney(totals.pending)}</div>
            </div>
            <div data-testid="master-total-paid" className="rounded-2xl bg-white p-5 border-2 border-emerald-100">
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Paid</div>
              <div className="text-3xl font-extrabold text-emerald-700 mt-1">{fmtMoney(totals.paid)}</div>
            </div>
            <div data-testid="master-total-amount" className="rounded-2xl bg-white p-5 border-2 border-violet-100">
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Total</div>
              <div className="text-3xl font-extrabold text-violet-700 mt-1">{fmtMoney(totals.total)}</div>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-x-auto">
            {loading ? (
              <div className="p-10 flex items-center justify-center text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
              </div>
            ) : rows.length === 0 ? (
              <div className="p-10 text-center text-slate-500" data-testid="master-empty">
                No compensation ledger entries for this period.
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="master-table">
                <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
                  <tr>
                    <th className="text-left px-3 py-2">Event Date</th>
                    <th className="text-left px-3 py-2">Employee</th>
                    <th className="text-left px-3 py-2">Company</th>
                    <th className="text-left px-3 py-2">Role</th>
                    <th className="text-left px-3 py-2">Deal</th>
                    <th className="text-left px-3 py-2">Comp Event</th>
                    <th className="text-right px-3 py-2">Amount</th>
                    <th className="text-center px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">Plan</th>
                    <th className="text-left px-3 py-2">Ver.</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.compensation_event_id}
                        data-testid={`master-row-${r.compensation_event_id}`}
                        className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.event_date)}</td>
                      <td className="px-3 py-2">{r.employee_name || r.employee_id}</td>
                      <td className="px-3 py-2">{r.company_name || '—'}</td>
                      <td className="px-3 py-2 capitalize">{(r.role || '').replaceAll('_', ' ')}</td>
                      <td className="px-3 py-2">{r.deal_name || r.deal_id}</td>
                      <td className="px-3 py-2 font-mono text-xs">{r.event}</td>
                      <td className="px-3 py-2 text-right font-semibold">{fmtMoney(r.amount)}</td>
                      <td className="px-3 py-2 text-center"><StatusPill status={r.status} /></td>
                      <td className="px-3 py-2">{r.compensation_plan_code || '—'}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">v{r.compensation_plan_version || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
